import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, Images } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/context/AuthContext";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;

export default function GalleryManager({ open, onOpenChange }) {
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => axios.get(`${API}/gallery`).then(({ data }) => setPhotos(data)).catch(() => {}), []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    let ok = 0;
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      form.append("caption", caption);
      try {
        await axios.post(`${API}/gallery`, form, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail, `Upload failed: ${file.name}`));
      }
    }
    if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} uploaded`);
    setCaption("");
    setBusy(false);
    load();
  };

  const remove = async (p) => {
    if (!window.confirm("Remove this photo from the website?")) return;
    await axios.delete(`${API}/gallery/${p.id}`);
    toast.success("Photo removed");
    load();
  };

  const saveCaption = async (p, value) => {
    if (value === p.caption) return;
    await axios.patch(`${API}/gallery/${p.id}`, { caption: value });
    load();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-[#111827] border-slate-700 text-slate-100 max-h-[90vh] overflow-y-auto" data-testid="gallery-manager">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold">Website Gallery</DialogTitle>
          <DialogDescription className="text-slate-400">Photos shown under "Our Work" on the public website. JPG / PNG / WEBP, up to 8 MB each.</DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-dashed border-slate-700 bg-slate-950/30 p-4 flex flex-col sm:flex-row gap-2 items-stretch">
          <Input placeholder="Caption for the photo(s) — e.g. Honda Civic full respray, Championship White" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="gallery-caption-input" className="bg-slate-950/60 border-slate-700 h-10 text-sm flex-1" />
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => upload([...e.target.files])} data-testid="gallery-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="gallery-upload-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold h-10"><Upload className="w-4 h-4 mr-2" /> {busy ? "Uploading…" : "Upload photos"}</Button>
        </div>

        {photos.length === 0 ? (
          <div className="text-center text-slate-500 py-10 text-sm" data-testid="gallery-empty"><Images className="w-8 h-8 mx-auto mb-2 text-slate-600" />No photos yet.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="gallery-grid">
            {photos.map((p) => (
              <div key={p.id} className="rounded-xl overflow-hidden border border-slate-800 bg-slate-950/40" data-testid={`gallery-item-${p.id}`}>
                <img src={`${BACKEND_URL}${p.url}`} alt={p.caption} className="w-full aspect-[4/3] object-cover" />
                <div className="p-2 flex gap-1">
                  <Input defaultValue={p.caption} placeholder="Caption" onBlur={(e) => saveCaption(p, e.target.value)} data-testid={`gallery-caption-${p.id}`} className="bg-slate-950/60 border-slate-700 h-8 text-xs" />
                  <button onClick={() => remove(p)} data-testid={`gallery-delete-${p.id}`} className="h-8 w-8 grid place-items-center rounded-md text-slate-500 hover:text-rose-400 hover:bg-slate-800 shrink-0"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
